/** Feature-local CRM labels for corporate Client nested surfaces (C2x). */

export const CONTACT_TYPE_LABELS = {
  phone: "Телефон",
  email: "Email",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
};

export const CONTACT_TYPE_OPTIONS = [
  { value: "phone", label: "Телефон" },
  { value: "email", label: "Email" },
  { value: "telegram", label: "Telegram" },
  { value: "whatsapp", label: "WhatsApp" },
];

export const contactTypeLabel = (type) =>
  CONTACT_TYPE_LABELS[type] || type || "—";

export const DEAL_TYPE_LABELS = {
  sale: "Продаж",
  rent: "Оренда",
};

export const PROPERTY_TYPE_LABELS = {
  apartment: "Квартира",
  house: "Будинок",
  land: "Земля",
  commercial: "Комерція",
  industrial: "Промислова",
};

export const DEAL_TYPE_OPTIONS = [
  { value: "", label: "— не обрано —" },
  { value: "sale", label: "Продаж" },
  { value: "rent", label: "Оренда" },
];

export const PROPERTY_TYPE_OPTIONS = [
  { value: "", label: "— не обрано —" },
  { value: "apartment", label: "Квартира" },
  { value: "house", label: "Будинок" },
  { value: "land", label: "Земля" },
  { value: "commercial", label: "Комерція" },
  { value: "industrial", label: "Промислова" },
];

export const COMMUNICATION_TYPE_LABELS = {
  call: "Дзвінок",
  email: "Email",
  message: "Повідомлення",
};

export const COMMUNICATION_DIRECTION_LABELS = {
  in: "Вхідне",
  out: "Вихідне",
};

export const COMMUNICATION_TYPE_OPTIONS = [
  { value: "call", label: "Дзвінок" },
  { value: "email", label: "Email" },
  { value: "message", label: "Повідомлення" },
];

export const COMMUNICATION_DIRECTION_OPTIONS = [
  { value: "out", label: "Вихідне" },
  { value: "in", label: "Вхідне" },
];

export const communicationTypeLabel = (type) =>
  COMMUNICATION_TYPE_LABELS[type] || type || "—";

export const communicationDirectionLabel = (direction) =>
  COMMUNICATION_DIRECTION_LABELS[direction] || direction || "—";

const hasText = (value) =>
  typeof value === "string" && value.trim().length > 0;

const hasNumber = (value) =>
  value !== null && value !== undefined && value !== "";

const formatPrice = (value) => {
  if (!hasNumber(value)) return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString("uk-UA", { maximumFractionDigits: 0 });
};

const formatArea = (value) => {
  if (!hasNumber(value)) return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString("uk-UA", { maximumFractionDigits: 2 });
};

const formatRooms = (value) => {
  if (!hasNumber(value)) return null;
  return String(value);
};

const formatRange = (min, max, formatter, suffix) => {
  const minStr = formatter(min);
  const maxStr = formatter(max);
  if (minStr && maxStr) {
    return suffix ? `${minStr} — ${maxStr} ${suffix}` : `${minStr} — ${maxStr}`;
  }
  if (minStr) return suffix ? `від ${minStr} ${suffix}` : `від ${minStr}`;
  if (maxStr) return suffix ? `до ${maxStr} ${suffix}` : `до ${maxStr}`;
  return null;
};

/** Human-readable preference rows for corporate need section. */
export function buildCorporateNeedRows(prefs) {
  if (!prefs) return [];

  const dealTypeLabel = hasText(prefs.deal_type)
    ? DEAL_TYPE_LABELS[prefs.deal_type] || prefs.deal_type
    : null;
  const propertyTypeLabel = hasText(prefs.property_type)
    ? PROPERTY_TYPE_LABELS[prefs.property_type] || prefs.property_type
    : null;
  const priceLabel = formatRange(prefs.min_price, prefs.max_price, formatPrice);
  const areaLabel = formatRange(
    prefs.min_area,
    prefs.max_area,
    formatArea,
    "м²",
  );
  const roomsLabel = formatRange(prefs.rooms_min, prefs.rooms_max, formatRooms);
  const cityLabel = hasText(prefs.city) ? prefs.city : null;
  const districtLabel = hasText(prefs.district) ? prefs.district : null;

  return [
    dealTypeLabel ? { label: "Тип угоди", value: dealTypeLabel } : null,
    propertyTypeLabel
      ? { label: "Тип нерухомості", value: propertyTypeLabel }
      : null,
    priceLabel ? { label: "Бюджет", value: priceLabel } : null,
    areaLabel ? { label: "Площа", value: areaLabel } : null,
    roomsLabel ? { label: "Кімнат", value: roomsLabel } : null,
    cityLabel ? { label: "Місто / населений пункт", value: cityLabel } : null,
    districtLabel ? { label: "Район", value: districtLabel } : null,
  ].filter(Boolean);
}

export const EMPTY_NEED_DRAFT = {
  deal_type: "",
  property_type: "",
  min_price: "",
  max_price: "",
  min_area: "",
  max_area: "",
  rooms_min: "",
  rooms_max: "",
  city: "",
  district: "",
};

export function needDraftFromPreferences(prefs) {
  if (!prefs) return { ...EMPTY_NEED_DRAFT };
  return {
    deal_type: prefs.deal_type || "",
    property_type: prefs.property_type || "",
    min_price:
      prefs.min_price === null || prefs.min_price === undefined
        ? ""
        : String(prefs.min_price),
    max_price:
      prefs.max_price === null || prefs.max_price === undefined
        ? ""
        : String(prefs.max_price),
    min_area:
      prefs.min_area === null || prefs.min_area === undefined
        ? ""
        : String(prefs.min_area),
    max_area:
      prefs.max_area === null || prefs.max_area === undefined
        ? ""
        : String(prefs.max_area),
    rooms_min:
      prefs.rooms_min === null || prefs.rooms_min === undefined
        ? ""
        : String(prefs.rooms_min),
    rooms_max:
      prefs.rooms_max === null || prefs.rooms_max === undefined
        ? ""
        : String(prefs.rooms_max),
    city: prefs.city || "",
    district: prefs.district || "",
  };
}

const parseOptionalNumber = (raw) => {
  if (raw === null || raw === undefined) return null;
  const trimmed = String(raw).trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return NaN;
  return n;
};

export function buildNeedPayload(draft) {
  return {
    deal_type: draft.deal_type || "",
    property_type: draft.property_type || "",
    min_price: parseOptionalNumber(draft.min_price),
    max_price: parseOptionalNumber(draft.max_price),
    min_area: parseOptionalNumber(draft.min_area),
    max_area: parseOptionalNumber(draft.max_area),
    rooms_min: parseOptionalNumber(draft.rooms_min),
    rooms_max: parseOptionalNumber(draft.rooms_max),
    city: (draft.city || "").trim(),
    district: (draft.district || "").trim(),
  };
}

export function formatCommunicationTimestamp(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("uk-UA");
}
